import { MarketingLayout } from '@/components/layout/MarketingLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Link } from 'wouter';
import { Check, ArrowRight, Mail, Database, Zap, Key, ExternalLink } from 'lucide-react';
import { SEO } from '@/components/SEO';
import { useTierInfo } from '@/hooks/useTierInfo';

export default function IntegrationsPage() {
  const { tierInfo } = useTierInfo();

  return (
    <MarketingLayout>
      <SEO
        title="Integrations | Prozpector"
        description="Prozpector integrates seamlessly with LinkedIn, PhantomBuster, and your favorite CRM tools. Streamline your cold prospecting workflow."
        url="https://prozpector.com/integrations"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white to-gray-50 py-20 md:py-32">
        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
              Bring Your Own Keys.
              <br />
              Own Your Data. 💤
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto">
              Prozpector integrates with the best email and enrichment providers. Use your own API keys for complete control, transparency, and lower costs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="text-lg px-8 py-6">
                  Book a Discovery Call <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="text-lg px-8 py-6">
                  Get a Quote
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* BYOK Benefits */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why BYOK (Bring Your Own Keys)?</h2>
            <p className="text-xl text-gray-600">Complete transparency and control over your prospecting infrastructure</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="p-6">
              <div className="h-12 w-12 bg-black rounded-lg flex items-center justify-center mb-4">
                <Key className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Own Your Infrastructure</h3>
              <p className="text-gray-600">
                Your API keys, your accounts, your data. Never worry about vendor lock-in or opaque pricing markup.
              </p>
            </Card>

            <Card className="p-6">
              <div className="h-12 w-12 bg-black rounded-lg flex items-center justify-center mb-4">
                <Database className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Lower Total Cost</h3>
              <p className="text-gray-600">
                Pay only the direct API costs with no markup. Most competitors charge 3-5x the actual provider costs.
              </p>
            </Card>

            <Card className="p-6">
              <div className="h-12 w-12 bg-black rounded-lg flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Full Transparency</h3>
              <p className="text-gray-600">
                See exactly how many credits you're using and what you're paying. No hidden fees or surprise charges.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Email Sending Providers */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-4">
              <Mail className="h-8 w-8" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Email Sending Providers</h2>
            <p className="text-xl text-gray-600">Choose from industry-leading email infrastructure providers</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {/* SendGrid */}
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold">SendGrid</h3>
                <ExternalLink className="h-5 w-5 text-gray-400" />
              </div>
              <p className="text-gray-600 mb-4">
                Twilio's enterprise-grade email infrastructure with 99.9% uptime SLA.
              </p>
              <div className="space-y-2 mb-6">
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">100 emails/day free</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">$0.0006 per email after</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Advanced analytics included</span>
                </div>
              </div>
              <a href="https://sendgrid.com" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full">
                  Learn More <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </Card>

            {/* Amazon SES */}
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold">Amazon SES</h3>
                <ExternalLink className="h-5 w-5 text-gray-400" />
              </div>
              <p className="text-gray-600 mb-4">
                AWS's highly scalable and cost-effective email service.
              </p>
              <div className="space-y-2 mb-6">
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">$0.10 per 1,000 emails</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Most cost-effective option</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Scales infinitely</span>
                </div>
              </div>
              <a href="https://aws.amazon.com/ses/" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full">
                  Learn More <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </Card>

            {/* Resend */}
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold">Resend</h3>
                <ExternalLink className="h-5 w-5 text-gray-400" />
              </div>
              <p className="text-gray-600 mb-4">
                Modern email API built for developers with best-in-class DX.
              </p>
              <div className="space-y-2 mb-6">
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">3,000 emails/month free</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">$20/month for 50k emails</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Beautiful email builder</span>
                </div>
              </div>
              <a href="https://resend.com" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full">
                  Learn More <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </Card>
          </div>
        </div>
      </section>

      {/* Email Enrichment Providers */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-4">
              <Database className="h-8 w-8" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Email Enrichment Providers</h2>
            <p className="text-xl text-gray-600">Find and verify professional email addresses at scale</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Apollo.io */}
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold">Apollo.io</h3>
                <ExternalLink className="h-5 w-5 text-gray-400" />
              </div>
              <p className="text-gray-600 mb-4">
                275M+ contacts with 65M+ companies. Industry-leading B2B database.
              </p>
              <div className="space-y-2 mb-6">
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">50 credits/month free</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">$49/mo for 1,200 credits</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">95%+ email accuracy</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Job title, company data included</span>
                </div>
              </div>
              <a href="https://apollo.io" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full">
                  Learn More <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </Card>

            {/* Hunter.io */}
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold">Hunter.io</h3>
                <ExternalLink className="h-5 w-5 text-gray-400" />
              </div>
              <p className="text-gray-600 mb-4">
                Focused email finder with domain search and verification tools.
              </p>
              <div className="space-y-2 mb-6">
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">25 searches/month free</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">$49/mo for 500 searches</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Email verification included</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Chrome extension available</span>
                </div>
              </div>
              <a href="https://hunter.io" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="w-full">
                  Learn More <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </Card>
          </div>
        </div>
      </section>

      {/* Setup Guide CTA */}
      <section className="py-16 bg-gray-50">
        <div className="container">
          <div className="max-w-3xl mx-auto bg-black text-white rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              We Handle the Integrations
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              You don't need to worry about API keys or setup. We manage all the technical integrations for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="bg-white text-black hover:bg-gray-100 border-white text-lg px-8 py-6">
                  Book a Discovery Call <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <Link href="/help">
                <Button size="lg" variant="ghost" className="text-white hover:bg-white/10 text-lg px-8 py-6">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Comparison */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">BYOK vs Traditional Pricing</h2>
            <p className="text-xl text-gray-600">See the massive savings with BYOK approach</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Traditional SaaS */}
              <Card className="p-6 border-2 border-gray-300">
                <h3 className="text-2xl font-bold mb-6 text-center">Traditional SaaS</h3>
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Platform Fee</span>
                    <span className="font-semibold">$299/mo</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email Sending</span>
                    <span className="font-semibold text-sm">Included (limited)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email Credits</span>
                    <span className="font-semibold text-sm">$0.15/credit</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">1000 enrichments</span>
                    <span className="font-semibold">$150</span>
                  </div>
                  <div className="border-t pt-4 flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span className="text-red-600">$449/mo</span>
                  </div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                  Hidden markup on API costs, vendor lock-in
                </div>
              </Card>

              {/* Prozpector BYOK */}
              <Card className="p-6 border-2 border-black shadow-lg">
                <h3 className="text-2xl font-bold mb-6 text-center">Prozpector (BYOK)</h3>
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Prozpector Platform</span>
                    <span className="font-semibold">${tierInfo?.price || 79}/mo</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email Sending (SES)</span>
                    <span className="font-semibold">$10/mo</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email Credits (Apollo)</span>
                    <span className="font-semibold text-sm">$0.04/credit</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">1000 enrichments</span>
                    <span className="font-semibold">$40</span>
                  </div>
                  <div className="border-t pt-4 flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span className="text-green-600">$129/mo</span>
                  </div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800 font-semibold">
                  💰 Save $320/month (71% savings!)
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Fill Your Calendar?
            </h2>
            <p className="text-xl text-gray-600 mb-10">
              Let's discuss your ideal customer profile and how we can start booking qualified meetings for your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="text-lg px-8 py-6">
                  Book a Discovery Call <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a href="https://cal.com/scott-dudley-ftors8/15min" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="text-lg px-8 py-6">
                  Get a Quote
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
